import { useState, useCallback, useRef, useEffect } from 'react';
import { sessions as sessionsApi } from '../api/client';
import {
  HorizontalLinePrimitive,
  TrendLinePrimitive,
  RectanglePrimitive,
  FibonacciPrimitive,
} from '../utils/drawingPrimitives';
import {
  VerticalLinePrimitive,
  ParallelChannelPrimitive,
  PitchforkPrimitive,
  FibExtensionPrimitive,
  FibFanPrimitive,
  FibTimeZonesPrimitive,
  FibArcsPrimitive,
  TrianglePrimitive,
  EllipsePrimitive,
  ArrowMarkerPrimitive,
  MeasurePrimitive,
} from '../utils/drawingPrimitivesExtra';
import {
  PositionPrimitive,
  ForecastPrimitive,
  DateRangePrimitive,
  DatePriceRangePrimitive,
  BarsPatternPrimitive,
  GhostFeedPrimitive,
  TextLabelPrimitive,
  AnchoredTextPrimitive,
  NotePrimitive,
  CalloutPrimitive,
  PriceLabelPrimitive,
} from '../utils/forecastingPrimitives';
import {
  LabeledPathPrimitive,
  CyclePrimitive,
  CycleLinesPrimitive,
  SineWavePrimitive,
} from '../utils/patternPrimitives';
import {
  BrushPrimitive,
  PolylinePrimitive,
  ArcPrimitive,
  ArrowLinePrimitive,
  CirclePrimitive,
  FilledRectPrimitive,
  FilledEllipsePrimitive,
} from '../utils/brushPrimitives';
import {
  GannBoxPrimitive,
  GannFanPrimitive,
  GannSquarePrimitive,
  RegressionTrendPrimitive,
  DisjointChannelPrimitive,
  FlatChannelPrimitive,
} from '../utils/gannPrimitives';

let nextId = 1;

/**
 * TradingView-style drawing tools.
 *
 * - Live preview while creating
 * - Hover detection + glow
 * - Click to select, Del to delete, Esc to cancel
 * - Drag to move entire drawing
 * - Drag handles to modify anchor points
 * - Right-click to delete
 * - Toolbar delete button when selected
 * - Ctrl+Z / Ctrl+Y undo/redo
 */
// ── Serialize a drawing to JSON-safe object ──
function serializeDrawing(d) {
  const prim = d.primitive;
  const out = {
    type: d.type, label: d.label, paneIndex: d.paneIndex ?? 0,
    color: prim.borderColor || prim.color,
    lineWidth: prim.lineWidth, lineStyle: prim.lineStyle,
  };

  // Single-value fields
  if (prim.price !== undefined) out.price = prim.price;
  if (prim.logical !== undefined) out.logical = prim.logical;
  if (prim.extend) out.extend = true;
  if (prim.filled) out.filled = true;
  if (prim.direction) out.direction = prim.direction;
  if (prim.text) out.text = prim.text;
  if (prim.strokeWidth) out.strokeWidth = prim.strokeWidth;
  if (prim.isHighlighter) out.isHighlighter = true;

  // Points (primitives use point1/point2 or pt1/pt2 depending on type)
  if (prim.point) out.point = prim.point;
  if (prim.point1) out.pt1 = prim.point1;
  else if (prim.pt1) out.pt1 = prim.pt1;
  if (prim.point2) out.pt2 = prim.point2;
  else if (prim.pt2) out.pt2 = prim.pt2;
  if (prim.point3) out.pt3 = prim.point3;
  else if (prim.pt3) out.pt3 = prim.pt3;
  if (prim.pt4) out.pt4 = prim.pt4;
  if (prim.anchorPoint) out.point = prim.anchorPoint;
  if (prim.bubblePoint) out.pt2 = prim.bubblePoint;

  // Array of points (brush, polyline, forecast, patterns)
  if (prim.points && Array.isArray(prim.points)) out.points = prim.points;

  return out;
}

// ── Creators map for deserializing drawings ──
const CREATORS = {
  hline: (d) => new HorizontalLinePrimitive(d.price, { color: d.color, lineWidth: d.lineWidth, lineStyle: d.lineStyle }),
  vline: (d) => new VerticalLinePrimitive(d.logical, { color: d.color, lineWidth: d.lineWidth, lineStyle: d.lineStyle }),
  trendline: (d) => new TrendLinePrimitive(d.pt1, d.pt2, { color: d.color, lineWidth: d.lineWidth, lineStyle: d.lineStyle }),
  ray: (d) => new TrendLinePrimitive(d.pt1, d.pt2, { color: d.color, extend: true, lineWidth: d.lineWidth, lineStyle: d.lineStyle }),
  rectangle: (d) => new RectanglePrimitive(d.pt1, d.pt2, { borderColor: d.color, lineWidth: d.lineWidth, lineStyle: d.lineStyle }),
  fibonacci: (d) => new FibonacciPrimitive(d.pt1, d.pt2, { color: d.color, lineWidth: d.lineWidth, lineStyle: d.lineStyle }),
  channel: (d) => new ParallelChannelPrimitive(d.pt1, d.pt2, d.pt3, { color: d.color, lineWidth: d.lineWidth, lineStyle: d.lineStyle }),
  pitchfork: (d) => new PitchforkPrimitive(d.pt1, d.pt2, d.pt3, { color: d.color }),
  fibExtension: (d) => new FibExtensionPrimitive(d.pt1, d.pt2, { color: d.color, highPrice: d.pt1?.price, lowPrice: d.pt2?.price }),
  triangle: (d) => new TrianglePrimitive(d.pt1, d.pt2, d.pt3, { color: d.color }),
  brush: (d) => new BrushPrimitive(d.points, { color: d.color, strokeWidth: d.strokeWidth }),
  highlighter: (d) => new BrushPrimitive(d.points, { color: d.color, strokeWidth: d.strokeWidth, isHighlighter: true }),
  arc: (d) => new ArcPrimitive(d.pt1, d.pt2, { color: d.color }),
  arrowLine: (d) => new ArrowLinePrimitive(d.pt1, d.pt2, { color: d.color }),
  circle: (d) => new CirclePrimitive(d.pt1, d.pt2, { color: d.color, filled: d.filled }),
  filledRect: (d) => new FilledRectPrimitive(d.pt1, d.pt2, { color: d.color }),
  filledEllipse: (d) => new FilledEllipsePrimitive(d.pt1, d.pt2, { color: d.color }),
  ellipse: (d) => new EllipsePrimitive(d.pt1, d.pt2, { color: d.color }),
  arrowUp: (d) => new ArrowMarkerPrimitive(d.point, { color: d.color, direction: 'up' }),
  arrowDown: (d) => new ArrowMarkerPrimitive(d.point, { color: d.color, direction: 'down' }),
  priceLabel: (d) => new PriceLabelPrimitive(d.point, { color: d.color }),
  measure: (d) => new MeasurePrimitive(d.pt1, d.pt2, { color: d.color }),
  longPosition: (d) => new PositionPrimitive(d.pt1, d.pt2, { color: d.color, direction: 'long' }),
  shortPosition: (d) => new PositionPrimitive(d.pt1, d.pt2, { color: d.color, direction: 'short' }),
  textLabel: (d) => new TextLabelPrimitive(d.pt1 || d.point, { text: d.text || 'Text', color: d.color, showBg: true }),
  anchoredText: (d) => new AnchoredTextPrimitive(d.pt1 || d.point, { text: d.text || 'Note', color: d.color }),
  note: (d) => new NotePrimitive(d.pt1 || d.point, { text: d.text || 'Note', color: d.color }),
  callout: (d) => new CalloutPrimitive(d.pt1, d.pt2, { text: d.text || 'Callout', color: d.color }),
  polyline: (d) => new PolylinePrimitive(d.points, { color: d.color }),
  forecast: (d) => new ForecastPrimitive(d.points, { color: d.color }),
  barsPattern: (d) => new BarsPatternPrimitive(d.points, { color: d.color }),
  ghostFeed: (d) => new GhostFeedPrimitive(d.points, { color: d.color }),
  fibFan: (d) => new FibFanPrimitive(d.pt1, d.pt2, { color: d.color }),
  fibArcs: (d) => new FibArcsPrimitive(d.pt1, d.pt2, { color: d.color }),
  fibTimeZones: (d) => new FibTimeZonesPrimitive(d.pt1, d.pt2, { color: d.color }),
  dateRange: (d) => new DateRangePrimitive(d.pt1, d.pt2, { color: d.color }),
  datePriceRange: (d) => new DatePriceRangePrimitive(d.pt1, d.pt2, { color: d.color }),
  cycle: (d) => new CyclePrimitive(d.pt1, d.pt2, { color: d.color }),
  cycleLines: (d) => new CycleLinesPrimitive(d.pt1, d.pt2, { color: d.color }),
  sineWave: (d) => new SineWavePrimitive(d.pt1, d.pt2, { color: d.color }),
  gannBox: (d) => new GannBoxPrimitive(d.pt1, d.pt2, { color: d.color }),
  gannFan: (d) => new GannFanPrimitive(d.pt1, d.pt2, { color: d.color }),
  gannSquare: (d) => new GannSquarePrimitive(d.pt1, d.pt2, { color: d.color }),
  regressionTrend: (d) => new RegressionTrendPrimitive(d.pt1, d.pt2, { color: d.color }),
  disjointChannel: (d) => new DisjointChannelPrimitive(d.pt1, d.pt2, d.pt3, d.pt4 || null, { color: d.color }),
  flatChannel: (d) => new FlatChannelPrimitive(d.pt1, d.pt2, d.pt3, { color: d.color }),
};

const SAVE_DEBOUNCE = 2000;

export default function useDrawingTools(chartRef, candles, sessionId, initialDrawingConfig) {
  const [activeTool, setActiveTool] = useState(null);
  const activeToolRef = useRef(null);
  activeToolRef.current = activeTool;
  const [magnetMode, setMagnetMode] = useState('off'); // 'off' | 'weak' | 'strong'
  const candlesRef = useRef(candles || []);
  candlesRef.current = candles || [];
  const [drawings, setDrawings] = useState([]);
  const [pendingPoint, setPendingPoint] = useState(null);
  const [drawingColor, setDrawingColor] = useState('#f59e0b');
  const [selectedId, setSelectedId] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);

  const [pendingPoints, setPendingPoints] = useState([]); // for 3-click tools
  const pendingPointsRef = useRef([]);
  pendingPointsRef.current = pendingPoints;

  const previewRef = useRef(null);
  const drawingsRef = useRef(drawings);
  drawingsRef.current = drawings;
  const saveTimerRef = useRef(null);
  const drawingsInitializedRef = useRef(false);
  const sessionIdRef = useRef(sessionId);
  sessionIdRef.current = sessionId;

  // Auto-save drawings (debounced) — uses refs to avoid stale closures
  const saveDrawings = useCallback(() => {
    if (!sessionIdRef.current) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      const serialized = drawingsRef.current.map(serializeDrawing).filter(Boolean);
      sessionsApi.saveDrawingConfig(sessionIdRef.current, serialized).catch((err) => {
        console.error('Failed to save drawings:', err);
      });
    }, SAVE_DEBOUNCE);
  }, []);

  // Load drawings from session config (retry until chart + panels are ready)
  useEffect(() => {
    if (!initialDrawingConfig || !initialDrawingConfig.length || drawingsInitializedRef.current) return;

    // Validate a drawing has required data before creating
    const isValid = (d) => {
      if (!CREATORS[d.type]) return false;
      const single = ['hline', 'vline', 'arrowUp', 'arrowDown', 'priceLabel'].includes(d.type);
      const multi = ['brush', 'highlighter', 'polyline', 'forecast', 'barsPattern', 'ghostFeed'].includes(d.type);
      if (!single && !multi && (!d.pt1 || !d.pt2)) return false;
      if (multi && (!d.points || !d.points.length)) return false;
      return true;
    };

    const validDrawings = initialDrawingConfig.filter(isValid);
    const maxPane = validDrawings.reduce((m, d) => Math.max(m, d.paneIndex ?? 0), 0);

    const tryLoad = () => {
      const series = chartRef?.current?.getCandleSeries?.();
      if (!series) return false;

      // Wait for panel series if drawings reference non-zero panes
      if (maxPane > 0) {
        for (let p = 1; p <= maxPane; p++) {
          const ps = chartRef?.current?.getSeriesForPane?.(p);
          if (!ps || ps === series) return false; // panel not ready
        }
      }

      drawingsInitializedRef.current = true;

      for (const d of validDrawings) {
        try {
          const prim = CREATORS[d.type](d);
          const targetSeries = (d.paneIndex && d.paneIndex > 0)
            ? chartRef.current.getSeriesForPane(d.paneIndex)
            : series;
          targetSeries.attachPrimitive(prim);
          setDrawings(prev => [...prev, {
            id: nextId++, type: d.type, primitive: prim,
            label: d.label || d.type, paneIndex: d.paneIndex ?? 0,
          }]);
        } catch {}
      }
      return true;
    };

    if (tryLoad()) return;

    // Retry every 300ms up to 10s
    let attempts = 0;
    const interval = setInterval(() => {
      attempts++;
      if (tryLoad() || attempts > 33) {
        clearInterval(interval);
        // If panels never appeared, load pane-0 drawings only
        if (!drawingsInitializedRef.current) {
          drawingsInitializedRef.current = true;
          const series = chartRef?.current?.getCandleSeries?.();
          if (!series) return;
          for (const d of validDrawings.filter(d => (d.paneIndex ?? 0) === 0)) {
            try {
              const prim = CREATORS[d.type](d);
              series.attachPrimitive(prim);
              setDrawings(prev => [...prev, {
                id: nextId++, type: d.type, primitive: prim,
                label: d.label || d.type, paneIndex: 0,
              }]);
            } catch {}
          }
        }
      }
    }, 300);
    return () => clearInterval(interval);
  }, [initialDrawingConfig, chartRef]);

  // Save on unmount
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      if (sessionId && drawingsRef.current.length > 0) {
        const serialized = drawingsRef.current.map(serializeDrawing).filter(Boolean);
        sessionsApi.saveDrawingConfig(sessionId, serialized).catch(() => {});
      }
    };
  }, [sessionId]);

  // ── Clipboard for copy/paste ──
  const clipboardRef = useRef(null); // serialized drawing data

  // ── Undo / Redo stack ──
  const undoStackRef = useRef([]); // { action: 'add'|'remove', drawing }
  const redoStackRef = useRef([]);

  // Drag state (not in React state to avoid re-renders during drag)
  const dragRef = useRef(null);
  // { drawingId, handleIndex (-1 = whole drawing), startLogical, startPrice, origPoints }

  const getSeries = useCallback((paneIndex) => {
    if (paneIndex != null && paneIndex > 0) {
      return chartRef?.current?.getSeriesForPane?.(paneIndex) || chartRef?.current?.getCandleSeries?.();
    }
    return chartRef?.current?.getCandleSeries?.();
  }, [chartRef]);
  const getChart = useCallback(() => chartRef?.current?.getChart?.(), [chartRef]);
  const activePaneRef = useRef(0);
  const getContainer = useCallback(() => {
    const r = chartRef?.current;
    return r?.current || r; // imperative handle exposes .current as the DOM node
  }, [chartRef]);

  const clearPreview = useCallback(() => {
    if (previewRef.current) {
      try { getSeries(activePaneRef.current)?.detachPrimitive(previewRef.current); } catch {}
      previewRef.current = null;
    }
  }, [getSeries]);

  const selectTool = useCallback((tool) => {
    clearPreview();
    setActiveTool(tool);
    setPendingPoint(null);
    setPendingPoints([]);
    setSelectedId(null);
    drawingsRef.current.forEach((d) => { d.primitive.isSelected = false; });
  }, [clearPreview]);

  const cancelTool = useCallback(() => {
    clearPreview();
    setActiveTool(null);
    setPendingPoint(null);
    setPendingPoints([]);
  }, [clearPreview]);

  // Track drawing additions for undo
  const pushUndo = useCallback((action, drawing) => {
    undoStackRef.current.push({ action, drawing });
    redoStackRef.current = []; // clear redo on new action
  }, []);

  const removeDrawing = useCallback((id) => {
    setDrawings((prev) => {
      const d = prev.find((d) => d.id === id);
      if (d) {
        try { getSeries(d.paneIndex ?? 0)?.detachPrimitive(d.primitive); } catch {}
        pushUndo('remove', d);
      }
      return prev.filter((d) => d.id !== id);
    });
    setSelectedId((cur) => cur === id ? null : cur);
    saveDrawings();
  }, [getSeries, pushUndo]);

  const undo = useCallback(() => {
    const entry = undoStackRef.current.pop();
    if (!entry) return;
    const series = getSeries(entry.drawing.paneIndex ?? 0);
    if (!series) return;
    if (entry.action === 'add') {
      try { series.detachPrimitive(entry.drawing.primitive); } catch {}
      setDrawings(prev => prev.filter(d => d.id !== entry.drawing.id));
      redoStackRef.current.push({ action: 'add', drawing: entry.drawing });
    } else if (entry.action === 'remove') {
      series.attachPrimitive(entry.drawing.primitive);
      setDrawings(prev => [...prev, entry.drawing]);
      redoStackRef.current.push({ action: 'remove', drawing: entry.drawing });
    }
    setSelectedId(null);
  }, [getSeries]);

  const redo = useCallback(() => {
    const entry = redoStackRef.current.pop();
    if (!entry) return;
    const series = getSeries(entry.drawing.paneIndex ?? 0);
    if (!series) return;
    if (entry.action === 'add') {
      series.attachPrimitive(entry.drawing.primitive);
      setDrawings(prev => [...prev, entry.drawing]);
      undoStackRef.current.push({ action: 'add', drawing: entry.drawing });
    } else if (entry.action === 'remove') {
      try { series.detachPrimitive(entry.drawing.primitive); } catch {}
      setDrawings(prev => prev.filter(d => d.id !== entry.drawing.id));
      undoStackRef.current.push({ action: 'remove', drawing: entry.drawing });
    }
    setSelectedId(null);
  }, [getSeries]);

  const clearAll = useCallback(() => {
    drawingsRef.current.forEach((d) => { try { getSeries(d.paneIndex ?? 0)?.detachPrimitive(d.primitive); } catch {} });
    clearPreview();
    setDrawings([]);
    setPendingPoint(null);
    setActiveTool(null);
    setSelectedId(null);
    undoStackRef.current = [];
    redoStackRef.current = [];
    saveDrawings();
  }, [getSeries, clearPreview, saveDrawings]);

  const toggleLock = useCallback((id) => {
    setDrawings(prev => prev.map(d => d.id === id ? { ...d, locked: !d.locked } : d));
  }, []);

  // Helper: add a drawing and push to undo stack
  const addDrawing = useCallback((drawing) => {
    if (drawing.paneIndex === undefined) drawing.paneIndex = activePaneRef.current;
    setDrawings(prev => [...prev, drawing]);
    pushUndo('add', drawing);
    saveDrawings();
  }, [pushUndo, saveDrawings]);

  // Load drawing template — recreate primitives from serialized data
  const loadDrawingTemplate = useCallback((templateDrawings) => {
    const series = getSeries(0);
    if (!series) return;
    for (const d of templateDrawings) {
      const creator = CREATORS[d.type];
      if (!creator) continue;
      // Validate
      const single = ['hline', 'vline', 'arrowUp', 'arrowDown', 'priceLabel'].includes(d.type);
      const multi = ['brush', 'highlighter', 'polyline', 'forecast', 'barsPattern', 'ghostFeed'].includes(d.type);
      if (!single && !multi && (!d.pt1 || !d.pt2)) continue;
      if (multi && (!d.points || !d.points.length)) continue;
      try {
        const prim = creator(d);
        const pane = d.paneIndex ?? 0;
        const targetSeries = pane > 0 ? (getSeries(pane) || series) : series;
        targetSeries.attachPrimitive(prim);
        addDrawing({ id: nextId++, type: d.type, primitive: prim, label: d.label || d.type, paneIndex: pane });
      } catch {}
    }
  }, [getSeries, addDrawing]);

  // ── Coordinate helpers ──
  const toScreen = useCallback((logical, price, paneIndex) => {
    const chart = getChart(), series = getSeries(paneIndex ?? activePaneRef.current);
    if (!chart || !series) return [0, 0];
    return [chart.timeScale().logicalToCoordinate(logical) ?? 0, series.priceToCoordinate(price) ?? 0];
  }, [getChart, getSeries]);

  // ── Magnet snap ──
  const snapPoint = useCallback((logical, price) => {
    if (magnetMode === 'off' || !activeTool) return { logical, price };
    const idx = Math.round(logical);
    const c = candlesRef.current;
    if (!c || idx < 0 || idx >= c.length) return { logical, price };
    const candle = c[idx];
    if (!candle) return { logical, price };

    if (magnetMode === 'weak') {
      // Snap to close only
      return { logical: idx, price: candle.close };
    }
    // Strong: snap to nearest OHLC
    const ohlc = [candle.open, candle.high, candle.low, candle.close];
    let best = candle.close, bestDist = Math.abs(price - candle.close);
    for (const v of ohlc) {
      const d = Math.abs(price - v);
      if (d < bestDist) { bestDist = d; best = v; }
    }
    return { logical: idx, price: best };
  }, [magnetMode, activeTool]);

  // ── Inline text editing state ──
  const [editingId, setEditingId] = useState(null);
  const editingIdRef = useRef(null);
  editingIdRef.current = editingId;

  const startEditingText = useCallback((drawingId) => {
    setEditingId(drawingId);
  }, []);

  const finishEditingText = useCallback((newText) => {
    if (editingIdRef.current != null) {
      const d = drawingsRef.current.find((d) => d.id === editingIdRef.current);
      if (d?.primitive?.setText && newText != null) {
        d.primitive.setText(newText);
      }
    }
    setEditingId(null);
  }, []);

  const updateEditingStyle = useCallback((props) => {
    if (editingIdRef.current == null) return;
    const d = drawingsRef.current.find((d) => d.id === editingIdRef.current);
    if (!d) return;
    const p = d.primitive;
    if (props.fontSize !== undefined) p.fontSize = props.fontSize;
    if (props.bold !== undefined) p.bold = props.bold;
    if (props.color !== undefined) p.color = props.color;
    if (props.showBg !== undefined) p.showBg = props.showBg;
    p._requestUpdate?.();
    saveDrawings();
  }, [saveDrawings]);

  // ── Chart click ──
  const lastToolClickRef = useRef(0);
  const handleChartClick = useCallback(({ logical: rawLogical, price: rawPrice, paneIndex }) => {
    if (rawPrice == null || rawLogical == null) return;

    // If dragging, ignore click (it fires on mouseup after drag)
    if (dragRef.current) return;

    // Deduplicate: native mousedown + lwc subscribeClick can both fire
    if (activeTool) {
      const now = Date.now();
      if (now - lastToolClickRef.current < 200) return;
      lastToolClickRef.current = now;
    }

    // Apply magnet snap (only on main pane)
    const shouldSnap = activeTool && (paneIndex == null || paneIndex === 0);
    const { logical, price } = shouldSnap ? snapPoint(rawLogical, rawPrice) : { logical: rawLogical, price: rawPrice };

    // No tool active → select/deselect
    if (!activeTool) {
      const clickPane = paneIndex ?? 0;
      const [sx, sy] = toScreen(logical, price, clickPane);
      const hit = drawingsRef.current.find((d) => (d.paneIndex ?? 0) === clickPane && d.primitive.isNear(sx, sy));
      drawingsRef.current.forEach((d) => { d.primitive.isSelected = false; });
      if (hit) {
        hit.primitive.isSelected = true;
        setSelectedId(hit.id);
      } else {
        setSelectedId(null);
      }
      getChart()?.timeScale()?.applyOptions({});
      return;
    }

    // Track which pane we're drawing on (set on first click of multi-click tools)
    if (!pendingPoint) {
      activePaneRef.current = paneIndex ?? 0;
    }

    const series = getSeries(activePaneRef.current);
    if (!series) return;
    const point = { logical, price };

    // ── Tool definitions ──
    // 1-click tools
    const ONE_CLICK = {
      hline: () => { const p = new HorizontalLinePrimitive(price, { color: drawingColor }); return { p, label: 'H-Line' }; },
      vline: () => { const p = new VerticalLinePrimitive(logical, { color: drawingColor }); return { p, label: 'V-Line' }; },
      arrowUp: () => { const p = new ArrowMarkerPrimitive(point, { color: drawingColor, direction: 'up' }); return { p, label: 'Arrow Up' }; },
      arrowDown: () => { const p = new ArrowMarkerPrimitive(point, { color: drawingColor, direction: 'down' }); return { p, label: 'Arrow Down' }; },
      priceLabel: () => { const p = new PriceLabelPrimitive(point, { color: drawingColor }); return { p, label: 'Price Label' }; },
    };

    if (ONE_CLICK[activeTool]) {
      clearPreview();
      const { p, label } = ONE_CLICK[activeTool]();
      series.attachPrimitive(p);
      // Force chart repaint so primitive appears instantly
      const chart = getChart();
      if (chart) chart.timeScale().applyOptions({});
      addDrawing({ id: nextId++, type: activeTool, primitive: p, label });
      return;
    }

    // 1-click text tools (place with default text, double-click to edit)
    const TEXT_TOOLS = {
      textLabel: () => { const id = nextId++; const p = new TextLabelPrimitive(point, { text: 'Text', color: drawingColor, showBg: true }); return { p, label: 'Text', id }; },
      anchoredText: () => { const id = nextId++; const p = new AnchoredTextPrimitive(point, { text: 'Note', color: drawingColor }); return { p, label: 'Anchored Text', id }; },
      note: () => { const id = nextId++; const p = new NotePrimitive(point, { text: 'Note', color: drawingColor }); return { p, label: 'Note', id }; },
    };

    if (TEXT_TOOLS[activeTool]) {
      clearPreview();
      const { p, label, id } = TEXT_TOOLS[activeTool]();
      series.attachPrimitive(p);
      p.isSelected = true;
      addDrawing({ id, type: activeTool, primitive: p, label });
      setSelectedId(id);
      setActiveTool(null); // deactivate tool immediately
      startEditingText(id); // open editor synchronously
      return;
    }

    // 2-click tools
    const TWO_CLICK = {
      trendline: { preview: (a, b) => new TrendLinePrimitive(a, b, { color: drawingColor }), label: 'Trend Line' },
      ray: { preview: (a, b) => new TrendLinePrimitive(a, b, { color: drawingColor, extend: true }), label: 'Ray' },
      rectangle: { preview: (a, b) => new RectanglePrimitive(a, b, { borderColor: drawingColor }), label: 'Rectangle' },
      ellipse: { preview: (a, b) => new EllipsePrimitive(a, b, { color: drawingColor }), label: 'Ellipse' },
      fibFan: { preview: (a, b) => new FibFanPrimitive(a, b, { color: drawingColor }), label: 'Fib Fan' },
      fibArcs: { preview: (a, b) => new FibArcsPrimitive(a, b, { color: drawingColor }), label: 'Fib Arcs' },
      fibTimeZones: { preview: (a, b) => new FibTimeZonesPrimitive(a, b, { color: drawingColor }), label: 'Fib Time Zones' },
      measure: { preview: (a, b) => new MeasurePrimitive(a, b, { color: drawingColor }), label: 'Measure' },
      longPosition: { preview: (a, b) => new PositionPrimitive(a, b, { color: drawingColor, direction: 'long' }), label: 'Long Position' },
      shortPosition: { preview: (a, b) => new PositionPrimitive(a, b, { color: drawingColor, direction: 'short' }), label: 'Short Position' },
      dateRange: { preview: (a, b) => new DateRangePrimitive(a, b, { color: drawingColor }), label: 'Date Range' },
      datePriceRange: { preview: (a, b) => new DatePriceRangePrimitive(a, b, { color: drawingColor }), label: 'Date & Price Range' },
      arc: { preview: (a, b) => new ArcPrimitive(a, b, { color: drawingColor }), label: 'Arc' },
      arrowLine: { preview: (a, b) => new ArrowLinePrimitive(a, b, { color: drawingColor }), label: 'Arrow' },
      circle: { preview: (a, b) => new CirclePrimitive(a, b, { color: drawingColor }), label: 'Circle' },
      filledRect: { preview: (a, b) => new FilledRectPrimitive(a, b, { color: drawingColor }), label: 'Filled Rect' },
      filledEllipse: { preview: (a, b) => new FilledEllipsePrimitive(a, b, { color: drawingColor }), label: 'Filled Ellipse' },
      cycle: { preview: (a, b) => new CyclePrimitive(a, b, { color: drawingColor }), label: 'Cycle' },
      cycleLines: { preview: (a, b) => new CycleLinesPrimitive(a, b, { color: drawingColor }), label: 'Cycle Lines' },
      sineWave: { preview: (a, b) => new SineWavePrimitive(a, b, { color: drawingColor }), label: 'Sine Wave' },
      gannBox: { preview: (a, b) => new GannBoxPrimitive(a, b, { color: drawingColor }), label: 'Gann Box' },
      gannFan: { preview: (a, b) => new GannFanPrimitive(a, b, { color: drawingColor }), label: 'Gann Fan' },
      gannSquare: { preview: (a, b) => new GannSquarePrimitive(a, b, { color: drawingColor }), label: 'Gann Square' },
      regressionTrend: { preview: (a, b) => { const p = new RegressionTrendPrimitive(a, b, { color: drawingColor }); p.setCandles(candlesRef.current); return p; }, label: 'Regression Trend' },
    };

    if (TWO_CLICK[activeTool]) {
      const def = TWO_CLICK[activeTool];
      if (!pendingPoint) {
        setPendingPoint(point);
        clearPreview();
        const pv = def.preview(point, point);
        series.attachPrimitive(pv);
        previewRef.current = pv;
      } else {
        clearPreview();
        const p = def.preview(pendingPoint, point);
        series.attachPrimitive(p);
        addDrawing({ id: nextId++, type: activeTool, primitive: p, label: def.label });
        setPendingPoint(null);
      }
      return;
    }

    // Callout (2 clicks: anchor point, then bubble position, dbl-click to edit)
    if (activeTool === 'callout') {
      if (!pendingPoint) {
        setPendingPoint(point);
        clearPreview();
        const pv = new CalloutPrimitive(point, point, { text: 'Callout', color: drawingColor });
        series.attachPrimitive(pv);
        previewRef.current = pv;
      } else {
        clearPreview();
        const id = nextId++;
        const p = new CalloutPrimitive(pendingPoint, point, { text: 'Callout', color: drawingColor });
        series.attachPrimitive(p);
        p.isSelected = true;
        addDrawing({ id, type: 'callout', primitive: p, label: 'Callout' });
        setSelectedId(id);
        setPendingPoint(null);
        setActiveTool(null);
        startEditingText(id);
      }
      return;
    }

    // Fibonacci Retracement (2 clicks with logical positions for bounded lines)
    if (activeTool === 'fibonacci') {
      if (!pendingPoint) {
        setPendingPoint(point);
        clearPreview();
        const pv = new FibonacciPrimitive(point, point, { color: drawingColor });
        series.attachPrimitive(pv);
        previewRef.current = pv;
      } else {
        clearPreview();
        const p = new FibonacciPrimitive(pendingPoint, point, { color: drawingColor });
        series.attachPrimitive(p);
        addDrawing({ id: nextId++, type: 'fibonacci', primitive: p, label: 'Fib Retracement' });
        setPendingPoint(null);
      }
      return;
    }

    // 3-click tools (channel, pitchfork, triangle, fib extension)
    const THREE_CLICK = {
      channel: { create: (a,b,c) => new ParallelChannelPrimitive(a,b,c, { color: drawingColor }), preview2: (a,b) => new ParallelChannelPrimitive(a,b,null, { color: drawingColor }), label: 'Channel' },
      pitchfork: { create: (a,b,c) => new PitchforkPrimitive(a,b,c, { color: drawingColor }), preview2: (a,b) => new PitchforkPrimitive(a,b,null, { color: drawingColor }), label: 'Pitchfork' },
      triangle: { create: (a,b,c) => new TrianglePrimitive(a,b,c, { color: drawingColor }), preview2: (a,b) => new TrianglePrimitive(a,b,null, { color: drawingColor }), label: 'Triangle' },
      fibExtension: { create: (a,b,c) => new FibExtensionPrimitive(a,b,c, { color: drawingColor }), preview2: (a,b) => new TrendLinePrimitive(a,b, { color: drawingColor }), label: 'Fib Extension' },
      flatChannel: { create: (a,b,c) => new FlatChannelPrimitive(a,b,c, { color: drawingColor }), preview2: (a,b) => new FlatChannelPrimitive(a,b,null, { color: drawingColor }), label: 'Flat Channel' },
    };

    if (THREE_CLICK[activeTool]) {
      const def = THREE_CLICK[activeTool];
      if (!pendingPoint) {
        // First click
        setPendingPoint(point);
        setPendingPoints([point]);
        clearPreview();
        const pv = def.preview2(point, point);
        series.attachPrimitive(pv);
        previewRef.current = pv;
      } else if (pendingPointsRef.current.length === 1) {
        // Second click — upgrade preview to 3-point version
        clearPreview();
        pendingPointsRef.current.push(point);
        setPendingPoints([...pendingPointsRef.current]);
        const pv = def.create(pendingPointsRef.current[0], point, point);
        series.attachPrimitive(pv);
        previewRef.current = pv;
      } else {
        // Third click — commit
        clearPreview();
        const pts = pendingPointsRef.current;
        const p = def.create(pts[0], pts[1], point);
        series.attachPrimitive(p);
        addDrawing({ id: nextId++, type: activeTool, primitive: p, label: def.label });
        setPendingPoint(null);
        setPendingPoints([]);
      }
      return;
    }

    // Disjoint Channel (4 clicks)
    if (activeTool === 'disjointChannel') {
      if (!pendingPoint) {
        setPendingPoint(point);
        setPendingPoints([point]);
        pendingPointsRef.current = [point];
        clearPreview();
        const pv = new TrendLinePrimitive(point, point, { color: drawingColor });
        series.attachPrimitive(pv);
        previewRef.current = pv;
      } else if (pendingPointsRef.current.length === 1) {
        clearPreview();
        pendingPointsRef.current.push(point);
        setPendingPoints([...pendingPointsRef.current]);
        const pv = new DisjointChannelPrimitive(pendingPointsRef.current[0], point, point, null, { color: drawingColor });
        series.attachPrimitive(pv);
        previewRef.current = pv;
      } else if (pendingPointsRef.current.length === 2) {
        pendingPointsRef.current.push(point);
        setPendingPoints([...pendingPointsRef.current]);
        if (previewRef.current?.setPoint) previewRef.current.setPoint(3, point);
        // Preview 4th point
        if (previewRef.current?.setPoint4) previewRef.current.setPoint4(point);
      } else {
        clearPreview();
        const pts = pendingPointsRef.current;
        const p = new DisjointChannelPrimitive(pts[0], pts[1], pts[2], point, { color: drawingColor });
        series.attachPrimitive(p);
        addDrawing({ id: nextId++, type: 'disjointChannel', primitive: p, label: 'Disjoint Channel' });
        setPendingPoint(null);
        setPendingPoints([]);
      }
      return;
    }

    // Fixed-N-click pattern/wave tools
    const PATTERN_LABELS = {
      xabcd:              { labels: ['X','A','B','C','D'], n: 5, label: 'XABCD Pattern', showFill: true },
      abcPattern:         { labels: ['A','B','C'], n: 3, label: 'ABC Pattern' },
      headShoulders:      { labels: ['LS','H','RS','NL','NL','S1','S2'], n: 7, label: 'Head & Shoulders', showFill: true },
      cypherPattern:      { labels: ['X','A','B','C','D'], n: 5, label: 'Cypher Pattern', showFill: true },
      triPattern:         { labels: ['A','B','C'], n: 3, label: 'Triangle Pattern' },
      threeDrives:        { labels: ['1','1r','2','2r','3','3r','end'], n: 7, label: '3 Drives' },
      elliottImpulse:     { labels: ['0','1','2','3','4','5'], n: 6, label: 'Elliott Impulse' },
      elliottCorrection:  { labels: ['0','A','B','C'], n: 4, label: 'Elliott Correction' },
      elliottTriangle:    { labels: ['0','A','B','C','D','E'], n: 6, label: 'Elliott Triangle', showFill: true },
      elliottDouble:      { labels: ['W','X','Y','X','Z'], n: 5, label: 'Elliott Double' },
      elliottTriple:      { labels: ['W','X','Y','X','Z','X','W'], n: 7, label: 'Elliott Triple' },
    };

    if (PATTERN_LABELS[activeTool]) {
      const cfg = PATTERN_LABELS[activeTool];
      if (!pendingPoint) {
        // First click
        setPendingPoint(point);
        setPendingPoints([point]);
        pendingPointsRef.current = [point];
        clearPreview();
        const pv = new LabeledPathPrimitive([point, point], { color: drawingColor, labels: cfg.labels, showFill: cfg.showFill, toolLabel: cfg.label });
        series.attachPrimitive(pv);
        previewRef.current = pv;
      } else if (pendingPointsRef.current.length < cfg.n - 1) {
        // Intermediate click — add point
        pendingPointsRef.current.push(point);
        setPendingPoints([...pendingPointsRef.current]);
        // Update preview
        if (previewRef.current) {
          previewRef.current.points = [...pendingPointsRef.current, point];
          previewRef.current._requestUpdate?.();
        }
      } else {
        // Final click — commit
        clearPreview();
        const allPts = [...pendingPointsRef.current, point];
        const p = new LabeledPathPrimitive(allPts, { color: drawingColor, labels: cfg.labels, showFill: cfg.showFill, toolLabel: cfg.label });
        series.attachPrimitive(p);
        addDrawing({ id: nextId++, type: activeTool, primitive: p, label: cfg.label });
        setPendingPoint(null);
        setPendingPoints([]);
      }
      return;
    }

    // Multi-click tools (forecast, barsPattern, ghostFeed) — click adds points, double-click finishes
    const MULTI_CLICK = {
      forecast: { create: (pts) => new ForecastPrimitive(pts, { color: drawingColor }), label: 'Forecast' },
      barsPattern: { create: (pts) => new BarsPatternPrimitive(pts, { color: drawingColor }), label: 'Bars Pattern' },
      ghostFeed: { create: (pts) => new GhostFeedPrimitive(pts, { color: drawingColor }), label: 'Ghost Feed' },
      polyline: { create: (pts) => new PolylinePrimitive(pts, { color: drawingColor }), label: 'Polyline' },
    };

    if (MULTI_CLICK[activeTool]) {
      const def = MULTI_CLICK[activeTool];
      if (!pendingPoint) {
        // First click — start
        setPendingPoint(point);
        setPendingPoints([point, point]); // second is preview
        pendingPointsRef.current = [point, point];
        clearPreview();
        const pv = def.create([point, point]);
        series.attachPrimitive(pv);
        previewRef.current = pv;
      } else {
        // Add a new point (the current preview point becomes fixed, add a new preview)
        const pts = pendingPointsRef.current;
        pts[pts.length - 1] = point; // fix the last point
        pts.push(point); // add new preview point
        pendingPointsRef.current = pts;
        setPendingPoints([...pts]);
        // Update the preview primitive
        if (previewRef.current?.addPoint) {
          previewRef.current.points = [...pts];
          previewRef.current._requestUpdate?.();
        }
      }
      return;
    }
  }, [activeTool, pendingPoint, drawingColor, getSeries, getChart, clearPreview, toScreen, startEditingText, snapPoint]);

  const handleChartClickRef = useRef(handleChartClick);
  handleChartClickRef.current = handleChartClick;

  // ── Double-click: finish multi-click tools OR edit text ──
  const handleChartDblClick = useCallback(({ logical, price, point: screenPoint, paneIndex } = {}) => {
    // If editing, ignore
    if (editingIdRef.current != null) return;

    // Multi-click tool finish
    const MULTI_TOOLS = ['forecast', 'barsPattern', 'ghostFeed', 'polyline'];
    if (activeTool && MULTI_TOOLS.includes(activeTool)) {
      const series = getSeries();
      if (!series) return;
      const pts = pendingPointsRef.current;
      if (pts.length < 3) return;
      const finalPts = pts.slice(0, -1);
      clearPreview();
      const MULTI_CLICK = {
        forecast: { create: (p) => new ForecastPrimitive(p, { color: drawingColor }), label: 'Forecast' },
        barsPattern: { create: (p) => new BarsPatternPrimitive(p, { color: drawingColor }), label: 'Bars Pattern' },
        ghostFeed: { create: (p) => new GhostFeedPrimitive(p, { color: drawingColor }), label: 'Ghost Feed' },
        polyline: { create: (p) => new PolylinePrimitive(p, { color: drawingColor }), label: 'Polyline' },
      };
      const def = MULTI_CLICK[activeTool];
      const p = def.create(finalPts);
      series.attachPrimitive(p);
      addDrawing({ id: nextId++, type: activeTool, primitive: p, label: def.label });
      setPendingPoint(null);
      setPendingPoints([]);
      return;
    }

    // Double-click on drawing → open editor
    if (!activeTool && screenPoint) {
      const sx = screenPoint.x, sy = screenPoint.y;
      const clickPane = paneIndex ?? 0;

      for (const d of drawingsRef.current) {
        if ((d.paneIndex ?? 0) !== clickPane) continue;
        if (!d.primitive.isNear(sx, sy)) continue;

        drawingsRef.current.forEach((dd) => { dd.primitive.isSelected = false; });
        d.primitive.isSelected = true;
        setSelectedId(d.id);

        if (d.primitive.isEditable) {
          startEditingText(d.id);
        } else {
          openPropsEditor(d.id);
        }
        return;
      }
    }

    // Legacy fallback for text editing (no screen point available)
    if (!activeTool && !screenPoint) {
      const chart = getChart(), series = getSeries(), container = getContainer();
      if (!chart || !series || !container) return;
      const rect = container.getBoundingClientRect();
    }
  }, [activeTool, drawingColor, getSeries, getChart, getContainer, clearPreview]);

  // ── Mouse move (live preview + hover + drag) ──
  const handleChartMouseMove = useCallback(({ logical: rawLogical, price: rawPrice, point: screenPoint, paneIndex: movePaneIndex }) => {
    if (rawLogical == null || rawPrice == null) return;

    // Apply magnet snap for preview (only when a tool is active and not dragging)
    const snapped = (activeTool && !dragRef.current) ? snapPoint(rawLogical, rawPrice) : { logical: rawLogical, price: rawPrice };
    const logical = snapped.logical, price = snapped.price;

    // === DRAGGING ===
    if (dragRef.current) {
      const d = dragRef.current;
      const drawing = drawingsRef.current.find((dr) => dr.id === d.drawingId);
      if (!drawing) return;
      const prim = drawing.primitive;

      if (d.handleIndex >= 0) {
        // Drag handle
        prim.moveHandle(d.handleIndex, logical, price);
      } else {
        // Move whole drawing
        const dL = logical - d.lastLogical;
        const dP = price - d.lastPrice;
        prim.moveBy(dL, dP);
        d.lastLogical = logical;
        d.lastPrice = price;
      }
      return;
    }

    // === LIVE PREVIEW ===
    if (previewRef.current && pendingPoint) {
      const p = { logical, price };
      const prim = previewRef.current;
      if (prim.setPoint4 && pendingPointsRef.current.length === 3) prim.setPoint4(p);
      else if (prim.setPreviewPoint) prim.setPreviewPoint(p);
      else if (prim.setPoint3) prim.setPoint3(p);
      else if (prim.setPoint) prim.setPoint(pendingPointsRef.current.length === 1 ? 2 : 3, p);
      else if (prim.updatePrices) prim.updatePrices(Math.max(pendingPoint.price, price), Math.min(pendingPoint.price, price));
    }

    // === HOVER ===
    if (!activeTool && screenPoint) {
      const sx = screenPoint.x, sy = screenPoint.y;
      const currentPane = movePaneIndex ?? 0;
      let newHovered = null;

      for (const d of drawingsRef.current) {
        if ((d.paneIndex ?? 0) !== currentPane) continue;
        if (d.primitive.isSelected && d.primitive.hitTestHandle(sx, sy) >= 0) {
          newHovered = d.id;
          break;
        }
        if (d.primitive.isNear(sx, sy)) {
          newHovered = d.id;
          break;
        }
      }

      if (newHovered !== hoveredId) {
        drawingsRef.current.forEach((d) => { d.primitive.isHovered = (d.id === newHovered); });
        setHoveredId(newHovered);
      }

      // Cursor
      const container = getContainer();
      if (container?.style) {
        if (newHovered) {
          const hd = drawingsRef.current.find((d) => d.id === newHovered);
          if (hd?.primitive.isSelected && hd.primitive.hitTestHandle(sx, sy) >= 0) {
            const hi = hd.primitive.hitTestHandle(sx, sy);
            // Handle 0 = move anchor, handle 1+ on text tools = resize
            if (hd.primitive.isEditable && hi >= 1) {
              container.style.cursor = 'nwse-resize';
            } else {
              container.style.cursor = 'grab';
            }
          } else {
            container.style.cursor = 'move';
          }
        } else {
          container.style.cursor = '';
        }
      }
    }
  }, [activeTool, pendingPoint, hoveredId, getContainer, snapPoint]);

  // ── Native mousedown/mouseup for drag ──
  const toolClickIntentRef = useRef(null);

  useEffect(() => {
    const container = getContainer();
    if (!container) return;

    // Freehand drawing ref
    const freehandRef = { current: null }; // { primitive, id }

    const onMouseDown = (e) => {
      if (e.button !== 0) return;

      const chart = getChart();
      if (!chart) return;

      const rect = container.getBoundingClientRect();
      const sx = e.clientX - rect.left;
      const sy = e.clientY - rect.top;

      // Detect pane from Y position
      let detectedPane = 0;
      const panes = chart.panes();
      if (panes && panes.length > 1) {
        let yOffset = 0;
        for (let p = 0; p < panes.length; p++) {
          const h = chart.paneSize(p)?.height || 0;
          if (sy < yOffset + h + 4) { detectedPane = p; break; }
          yOffset += h + 4;
        }
      }
      activePaneRef.current = detectedPane;
      const series = getSeries(detectedPane);
      if (!series) return;

      // Start freehand brush/highlighter
      const currentTool = activeToolRef.current;
      if (currentTool === 'brush' || currentTool === 'highlighter') {
        e.preventDefault();
        e.stopPropagation();
        chart.applyOptions({ handleScroll: false, handleScale: false });
        const logical = chart.timeScale().coordinateToLogical(sx);
        const price = series.coordinateToPrice(sy);
        const pt = { logical, price };
        const isHL = currentTool === 'highlighter';
        const prim = new BrushPrimitive([pt], {
          color: drawingColor,
          strokeWidth: isHL ? 8 : 2,
          isHighlighter: isHL,
        });
        series.attachPrimitive(prim);
        const id = nextId++;
        freehandRef.current = { primitive: prim, id, type: currentTool, label: isHL ? 'Highlighter' : 'Brush' };
        return;
      }

      if (currentTool) {
        // Handle tool click on mouseup (not mousedown) to avoid blocking lwc internals
        // Store intent, execute on mouseup
        toolClickIntentRef.current = { sx, sy, detectedPane, series };
        return;
      }

      // Compute pane Y offsets for hit testing
      const paneYOffsets = [0];
      if (panes && panes.length > 1) {
        let accum = 0;
        for (let p = 0; p < panes.length - 1; p++) {
          accum += (chart.paneSize(p)?.height || 0) + 4;
          paneYOffsets.push(accum);
        }
      }

      // Check selected drawing handles first (skip locked)
      for (const d of drawingsRef.current) {
        if (!d.primitive.isSelected || d.locked) continue;
        const dOffset = paneYOffsets[d.paneIndex ?? 0] || 0;
        const hi = d.primitive.hitTestHandle(sx, sy - dOffset);
        if (hi >= 0) {
          e.preventDefault();
          e.stopPropagation();
          chart.applyOptions({ handleScroll: false, handleScale: false });
          container.style.cursor = 'grabbing';
          const dSeries = getSeries(d.paneIndex ?? 0);
          const logical = chart.timeScale().coordinateToLogical(sx);
          const price = dSeries.coordinateToPrice(sy - dOffset);
          dragRef.current = {
            drawingId: d.id,
            handleIndex: hi,
            lastLogical: logical,
            lastPrice: price,
          };
          return;
        }
      }

      // Check drawing body for move (skip locked)
      for (const d of drawingsRef.current) {
        if (d.locked) continue;
        const dOffset = paneYOffsets[d.paneIndex ?? 0] || 0;
        if (d.primitive.isNear(sx, sy - dOffset)) {
          e.preventDefault();
          e.stopPropagation();
          chart.applyOptions({ handleScroll: false, handleScale: false });
          container.style.cursor = 'grabbing';
          const dSeries = getSeries(d.paneIndex ?? 0);
          const logical = chart.timeScale().coordinateToLogical(sx);
          const price = dSeries.coordinateToPrice(sy - dOffset);

          // Select it too
          drawingsRef.current.forEach((dd) => { dd.primitive.isSelected = false; });
          d.primitive.isSelected = true;
          setSelectedId(d.id);

          dragRef.current = {
            drawingId: d.id,
            handleIndex: -1,
            lastLogical: logical,
            lastPrice: price,
          };
          return;
        }
      }
    };

    const onNativeMouseMove = (e) => {
      if (!freehandRef.current) return;
      const chart = getChart(); const series = getSeries(activePaneRef.current);
      if (!chart || !series) return;
      const rect = container.getBoundingClientRect();
      const sx = e.clientX - rect.left, sy = e.clientY - rect.top;
      const logical = chart.timeScale().coordinateToLogical(sx);
      const price = series.coordinateToPrice(sy);
      freehandRef.current.primitive.addPoint({ logical, price });
    };

    const onMouseUp = (e) => {
      // Execute tool click intent (deferred from mousedown to not block lwc)
      if (toolClickIntentRef.current && !dragRef.current && !freehandRef.current) {
        const { sx, sy, detectedPane, series } = toolClickIntentRef.current;
        toolClickIntentRef.current = null;
        const chart = getChart();
        if (chart && series) {
          const logical = chart.timeScale().coordinateToLogical(sx);
          const price = series.coordinateToPrice(sy);
          if (logical != null && price != null) {
            handleChartClickRef.current({ logical, price, point: { x: sx, y: sy }, paneIndex: detectedPane });
          }
        }
        return;
      }
      toolClickIntentRef.current = null;

      // Finish freehand
      if (freehandRef.current) {
        const { primitive, id, type, label } = freehandRef.current;
        if (primitive.points.length >= 2) {
          addDrawing({ id, type, primitive, label });
        } else {
          try { getSeries(activePaneRef.current)?.detachPrimitive(primitive); } catch {}
        }
        freehandRef.current = null;
        const chart = getChart();
        chart?.applyOptions({ handleScroll: true, handleScale: true });
        return;
      }

      if (dragRef.current) {
        dragRef.current = null;
        const chart = getChart();
        chart?.applyOptions({ handleScroll: true, handleScale: true });
        container.style.cursor = '';
        saveDrawings();
      }
    };

    // Right-click on drawing: select it (context menu handled by ReplayPage)
    const onContextMenu = (e) => {
      const rect = container.getBoundingClientRect();
      const sx = e.clientX - rect.left;
      const sy = e.clientY - rect.top;
      const hit = drawingsRef.current.find((d) => d.primitive.isNear(sx, sy));
      if (hit) {
        drawingsRef.current.forEach((d) => { d.primitive.isSelected = false; });
        hit.primitive.isSelected = true;
        setSelectedId(hit.id);
        hit.primitive._requestUpdate?.();
      }
    };

    const onDblClick = (e) => {
      const rect = container.getBoundingClientRect();
      const sx = e.clientX - rect.left;
      const sy = e.clientY - rect.top;
      const chart = getChart(), series = getSeries();
      if (chart && series) {
        const logical = chart.timeScale().coordinateToLogical(sx);
        const price = series.coordinateToPrice(sy);
        handleChartDblClick({ logical, price, point: { x: sx, y: sy }, paneIndex: 0 });
      } else {
        handleChartDblClick();
      }
    };

    container.addEventListener('mousedown', onMouseDown, true); // capture phase
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('mousemove', onNativeMouseMove);
    container.addEventListener('contextmenu', onContextMenu);
    container.addEventListener('dblclick', onDblClick, true);

    return () => {
      container.removeEventListener('mousedown', onMouseDown, true);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('mousemove', onNativeMouseMove);
      container.removeEventListener('contextmenu', onContextMenu);
      container.removeEventListener('dblclick', onDblClick, true);
    };
  }, [drawingColor, getChart, getSeries, getContainer, removeDrawing, handleChartDblClick, startEditingText]);

  // ── Copy / Paste / Duplicate ──
  const copyDrawing = useCallback((id) => {
    if (id == null) return;
    const d = drawingsRef.current.find((d) => d.id === id);
    if (!d) return;
    clipboardRef.current = { ...serializeDrawing(d), paneIndex: d.paneIndex ?? 0 };
  }, []);

  const pasteDrawing = useCallback((offset = 5) => {
    const data = clipboardRef.current;
    if (!data) return;
    const creator = CREATORS[data.type];
    if (!creator) return;
    const cloned = JSON.parse(JSON.stringify(data));
    const applyOffset = (pt) => {
      if (!pt) return pt;
      return { ...pt, logical: pt.logical + offset };
    };
    if (cloned.pt1) cloned.pt1 = applyOffset(cloned.pt1);
    if (cloned.pt2) cloned.pt2 = applyOffset(cloned.pt2);
    if (cloned.pt3) cloned.pt3 = applyOffset(cloned.pt3);
    if (cloned.pt4) cloned.pt4 = applyOffset(cloned.pt4);
    if (cloned.point) cloned.point = applyOffset(cloned.point);
    if (cloned.logical !== undefined) cloned.logical = cloned.logical + offset;
    if (cloned.points && Array.isArray(cloned.points)) {
      cloned.points = cloned.points.map(applyOffset);
    }
    try {
      const prim = creator(cloned);
      const pane = cloned.paneIndex ?? 0;
      const series = getSeries(pane) || getSeries(0);
      if (!series) return;
      series.attachPrimitive(prim);
      const newDrawing = { id: nextId++, type: cloned.type, primitive: prim, label: cloned.label || cloned.type, paneIndex: pane };
      addDrawing(newDrawing);
      drawingsRef.current.forEach((d) => { d.primitive.isSelected = false; });
      prim.isSelected = true;
      setSelectedId(newDrawing.id);
    } catch {}
  }, [getSeries, addDrawing]);

  const duplicateDrawing = useCallback((id) => {
    if (id == null) return;
    const d = drawingsRef.current.find((d) => d.id === id);
    if (!d) return;
    clipboardRef.current = { ...serializeDrawing(d), paneIndex: d.paneIndex ?? 0 };
    const data = clipboardRef.current;
    const creator = CREATORS[data.type];
    if (!creator) return;
    const cloned = JSON.parse(JSON.stringify(data));
    const applyOff = (pt) => pt ? { ...pt, logical: pt.logical + 5 } : pt;
    if (cloned.pt1) cloned.pt1 = applyOff(cloned.pt1);
    if (cloned.pt2) cloned.pt2 = applyOff(cloned.pt2);
    if (cloned.pt3) cloned.pt3 = applyOff(cloned.pt3);
    if (cloned.pt4) cloned.pt4 = applyOff(cloned.pt4);
    if (cloned.point) cloned.point = applyOff(cloned.point);
    if (cloned.logical !== undefined) cloned.logical += 5;
    if (cloned.points && Array.isArray(cloned.points)) cloned.points = cloned.points.map(applyOff);
    try {
      const prim = creator(cloned);
      const pane = cloned.paneIndex ?? 0;
      const series = getSeries(pane) || getSeries(0);
      if (!series) return;
      series.attachPrimitive(prim);
      const newDrawing = { id: nextId++, type: cloned.type, primitive: prim, label: cloned.label || cloned.type, paneIndex: pane };
      addDrawing(newDrawing);
      drawingsRef.current.forEach((dd) => { dd.primitive.isSelected = false; });
      prim.isSelected = true;
      setSelectedId(newDrawing.id);
    } catch {}
  }, [getSeries, addDrawing]);

  // ── Keyboard ──
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'Escape') {
        if (activeTool) cancelTool();
        else if (selectedId != null) {
          drawingsRef.current.forEach((d) => { d.primitive.isSelected = false; });
          setSelectedId(null);
        }
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId != null) {
        e.preventDefault();
        removeDrawing(selectedId);
      }
      // Copy: Ctrl+C (only when a drawing is selected)
      if (e.key === 'c' && (e.ctrlKey || e.metaKey) && selectedId != null) {
        e.preventDefault();
        copyDrawing(selectedId);
      }
      // Paste: Ctrl+V
      if (e.key === 'v' && (e.ctrlKey || e.metaKey) && !e.shiftKey && clipboardRef.current) {
        e.preventDefault();
        pasteDrawing();
      }
      // Duplicate: Ctrl+D
      if (e.key === 'd' && (e.ctrlKey || e.metaKey) && selectedId != null) {
        e.preventDefault();
        duplicateDrawing(selectedId);
      }
      // Undo: Ctrl+Z
      if (e.key === 'z' && (e.ctrlKey || e.metaKey) && !e.shiftKey) {
        e.preventDefault();
        undo();
      }
      // Redo: Ctrl+Y or Ctrl+Shift+Z
      if ((e.key === 'y' && (e.ctrlKey || e.metaKey)) || (e.key === 'z' && (e.ctrlKey || e.metaKey) && e.shiftKey)) {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [activeTool, selectedId, cancelTool, removeDrawing, undo, redo, copyDrawing, pasteDrawing, duplicateDrawing]);

  // ── Cursor for active tool ──
  useEffect(() => {
    const container = getContainer();
    if (!container?.style) return;
    if (activeTool) container.style.cursor = 'crosshair';
    else container.style.cursor = '';
    return () => { if (container?.style) container.style.cursor = ''; };
  }, [activeTool, getContainer]);

  // ── Update Fib levels for a drawing ──
  const updateDrawingLevels = useCallback((id, levels) => {
    const d = drawingsRef.current.find((d) => d.id === id);
    if (d?.primitive?.setLevels) {
      d.primitive.setLevels(levels);
    }
  }, []);

  const getDrawingLevels = useCallback((id) => {
    const d = drawingsRef.current.find((d) => d.id === id);
    return d?.primitive?.getLevels?.() || null;
  }, []);

  // ── Drawing property editor (double-click on non-text drawings) ──
  const [propsEditorId, setPropsEditorId] = useState(null);

  const openPropsEditor = useCallback((id) => {
    setPropsEditorId(id);
  }, []);

  const closePropsEditor = useCallback(() => {
    setPropsEditorId(null);
    saveDrawings();
  }, [saveDrawings]);

  const updateDrawingProps = useCallback((id, props) => {
    const d = drawingsRef.current.find((d) => d.id === id);
    if (!d) return;
    const prim = d.primitive;
    if (props.color !== undefined) {
      prim.color = props.color;
      if (prim.borderColor !== undefined) prim.borderColor = props.color;
    }
    if (props.lineWidth !== undefined) prim.lineWidth = props.lineWidth;
    if (props.lineStyle !== undefined) prim.lineStyle = props.lineStyle;
    if (props.price !== undefined) prim.price = props.price;
    prim._requestUpdate?.();
    saveDrawings();
  }, [saveDrawings]);

  // Get current props for editor
  const getDrawingProps = useCallback((id) => {
    const d = drawingsRef.current.find((d) => d.id === id);
    if (!d) return null;
    const prim = d.primitive;
    const out = {
      color: prim.borderColor || prim.color || '#f59e0b',
      lineWidth: prim.lineWidth ?? 2,
      lineStyle: prim.lineStyle ?? 0,
      type: d.type,
      label: d.label,
    };
    if (prim.price !== undefined) out.price = prim.price;
    return out;
  }, []);

  return {
    activeTool, selectTool, cancelTool,
    drawings, pendingPoint, pendingPoints,
    drawingColor, setDrawingColor,
    handleChartClick, handleChartMouseMove, handleChartDblClick,
    removeDrawing, clearAll, undo, redo, toggleLock, loadDrawingTemplate,
    copyDrawing, pasteDrawing, duplicateDrawing,
    selectedId, hoveredId,
    editingId, finishEditingText, updateEditingStyle,
    magnetMode, setMagnetMode,
    updateDrawingLevels, getDrawingLevels,
    propsEditorId, openPropsEditor, closePropsEditor, updateDrawingProps, getDrawingProps,
    getSerializedDrawings: () => drawingsRef.current.map(serializeDrawing).filter(Boolean),
  };
}
